/**
 * SDX Navigate - Client-side SPA navigation engine for Drupal.
 *
 * Entry point. Reads the config, constructs the collaborators,
 * assembles the navigation controller, installs the global event
 * listeners, and publishes the Drupal.sdxNavigation API object.
 *
 * Every piece of actual logic lives under navigate/ - this file
 * is deliberately a thin composition root so the wiring is
 * obvious and the controller's dependencies are explicit at
 * construction time rather than reached via module-level globals.
 *
 * Lifecycle events (kept for backward compatibility with the
 * previous IIFE implementation):
 *
 * - `sdx:navigate-start`  - fired before each fetch.
 * - `sdx:navigate-end`    - fired when page data is ready to render.
 * - `sdx:navigate-error`  - fired when the fetch fails.
 * - `sdx:confirm-modal`   - cancelable; themes with a confirm modal
 *                           call preventDefault to signal handling.
 *
 * Public API (installed on Drupal.sdxNavigation):
 *
 * - subscribe(listener)            - NavState store subscription.
 * - getState()                     - current NavState snapshot.
 * - navigate(url, options)         - returns a Promise<NavState>.
 * - prefetch(url)                  - warm the prefetch cache.
 * - abort()                        - cancel an in-flight navigation.
 * - isNavigating()                 - synchronous loading check.
 * - ready                          - Promise that resolves after
 *                                    the initial page state capture.
 * - registerComponentResolver(fn)  - framework modules register
 *                                    from their mount scripts.
 *
 * Backward-compat shortcut: `Drupal.sdxNavigate(url)` is preserved
 * as a fire-and-forget wrapper over navigate() for theme code that
 * uses the one-liner form.
 */

import { readNavigateConfig } from './navigate/config';
import { NavStateStore } from './navigate/nav-state-store';
import { PrefetchCache } from './navigate/prefetch-cache';
import { TagStore } from './navigate/tag-store';
import { RegionChecksums } from './navigate/region-checksums';
import { AssetInventory } from './navigate/asset-inventory';
import { ComponentResolverRegistry } from './navigate/component-resolver';
import { ProgressBar } from './navigate/progress-bar';
import { NavigationController } from './navigate/navigation-controller';
import {
  installClickListener,
  installFormListener,
  installHoverListener,
  installPopstateListener,
} from './navigate/listeners';
import { installViewportPrefetch } from './navigate/viewport-prefetch';
import { fetchPage } from './navigate/fetch-page';
import { isAnyFrameworkInDevMode } from './navigate/dev-mode';
import type { NavigableRules } from './navigate/url';
import type {
  ComponentResolver,
  NavState,
  NavigateOptions,
  SdxNavigationApi,
} from './navigate/types';

(function bootstrap(DrupalGlobal: DrupalGlobal, drupalSettingsGlobal: DrupalSettings): void {
  'use strict';

  const config = readNavigateConfig(drupalSettingsGlobal);
  if (!config) return;

  const state = new NavStateStore();
  const tagStore = new TagStore();
  const regionChecksums = new RegionChecksums();
  const inventory = new AssetInventory();
  const componentResolvers = new ComponentResolverRegistry();
  const progressBar = new ProgressBar();

  // The prefetch cache needs a fetcher bound to the shared tag
  // store and region checksums so the headers sent during a
  // prefetch match the headers sent on the eventual navigation.
  const prefetchCache = new PrefetchCache((url, signal, scope) =>
    fetchPage(url, signal, scope, [], {
      tagStore,
      regionChecksums,
      menuHash: '',
      routeName: '',
    }));

  const controller = new NavigationController({
    state,
    prefetchCache,
    tagStore,
    regionChecksums,
    inventory,
    componentResolvers,
    progressBar,
    contentSelector: config.contentSelector,
    devMode: isAnyFrameworkInDevMode(drupalSettingsGlobal),
  });

  const listenerRules: NavigableRules = { excludePattern: config.excludePattern };
  const listenerCtx = {
    controller,
    prefetchCache,
    rules: listenerRules,
    prefetchDelay: config.prefetchDelay,
    interceptForms: config.interceptForms,
    batchModal: config.batchModal,
    progressBar,
  };

  installClickListener(listenerCtx);
  installHoverListener(listenerCtx);
  installFormListener(listenerCtx);
  installPopstateListener(listenerCtx);
  if (config.viewportPrefetch) {
    installViewportPrefetch(listenerCtx);
  }

  controller.captureInitialState();

  const api: SdxNavigationApi = {
    subscribe: (listener) => state.subscribe(listener),
    getState: () => state.getState(),
    navigate: (url: string, options?: NavigateOptions): Promise<Readonly<NavState>> =>
      controller.start(url, options ?? {}),
    prefetch: (url: string) => controller.prefetch(url),
    abort: () => controller.abort(),
    isNavigating: () => state.isNavigating(),
    ready: Promise.resolve(),
    registerComponentResolver: (resolver: ComponentResolver) =>
      componentResolvers.register(resolver),
  };

  DrupalGlobal.sdxNavigation = api;

  // Backward-compat shortcut for theme code that uses the
  // one-liner `Drupal.sdxNavigate(url)` form. The Promise
  // returned by api.navigate() is intentionally dropped - the
  // caller does not care about completion.
  DrupalGlobal.sdxNavigate = (url: string, options?: { scope?: string; pushState?: boolean }) => {
    void api.navigate(url, options as NavigateOptions | undefined);
  };
})(Drupal, drupalSettings);
